/* global gsap */

import { useRef, useState, useEffect } from 'react';
import throttle from 'lodash/throttle';
import type { NavHandle } from '../Nav/Nav';

interface MenuOptions {
  offset?: number;
  delay?: number;
  delta?: number;
}

export default (options?: MenuOptions) => {
  const ref = useRef<NavHandle>(null);

  const { delay, offset, delta } = {
    offset: 0,
    delay: 1000,
    delta: 0.3,
    ...options,
  };
  const [limit, setLimit] = useState(window.innerHeight);
  const [dir, setDir] = useState<'up' | 'down' | null>(null);

  useEffect(() => {
    function onResize() {
      setLimit(window.innerHeight);
    }

    window.addEventListener('resize', onResize);

    return function cleanup() {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useEffect(() => {
    let prev = 0;
    let timeout: ReturnType<typeof setTimeout> | undefined;

    const onScroll = throttle(() => {
      clearTimeout(timeout);
      const currentScroll =
        document.documentElement.scrollTop || document.body.scrollTop;

      const diff = currentScroll - prev;
      setDir(diff > 0 ? 'down' : 'up');

      prev = currentScroll;
    }, 160);

    window.addEventListener('scroll', onScroll);
    onScroll();

    return function cleanup() {
      window.removeEventListener('scroll', onScroll);
      clearTimeout(timeout);
    };
  }, [delay, limit, offset]);

  useEffect(() => {
    function onScroll() {
      const opacity =
        (window.innerHeight - window.scrollY / delta) / window.innerHeight;

      if (opacity >= 0) {
        gsap.set(ref.current!.node, { opacity });
      } else {
        gsap.set(ref.current!.node, { opacity: 0 });
      }
    }

    if (dir === null) {
      window.addEventListener('scroll', onScroll);
      onScroll();
    }

    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [dir]);

  useEffect(() => {
    if (dir === 'up') {
      gsap.to(ref.current!.node, { opacity: 1, duration: 0.4 });
    } else if (dir === 'down') {
      ref.current!.close();
      gsap.to(ref.current!.node, { opacity: 0, duration: 0.4 });
    }
  }, [dir]);

  return ref;
};
